import { useEffect, useState } from "preact/hooks";
import { api } from "../api";
import { enablePush, pushSupported } from "../push";
import type {
  BloodPanelSummary, CoachNoteItem, CoachToday, MealSuggestion, SupplementStatus,
} from "@hh/shared";

function Gauge({ score, verdict }: { score: number; verdict: string }) {
  const r = 42, c = 2 * Math.PI * r;
  const color = verdict === "push" ? "var(--good)" : verdict === "hold" ? "var(--warn)" : "var(--bad)";
  return (
    <div class="gauge">
      <svg width="96" height="96" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r={r} fill="none" stroke="var(--surface-2)" stroke-width="8" />
        <circle cx="48" cy="48" r={r} fill="none" stroke={color} stroke-width="8" stroke-linecap="round"
          stroke-dasharray={c} stroke-dashoffset={c * (1 - score / 100)} transform="rotate(-90 48 48)"
          style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <div class="val"><div class="s">{score}</div><div class={`v verdict-${verdict}`}>{verdict}</div></div>
    </div>
  );
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  let bin = ""; const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

export function Coach({ reloadKey }: { reloadKey: number }) {
  const [c, setC] = useState<CoachToday | null>(null);
  const [supps, setSupps] = useState<SupplementStatus[]>([]);
  const [review, setReview] = useState<CoachNoteItem | null>(null);
  const [panels, setPanels] = useState<BloodPanelSummary[]>([]);
  const [suggestions, setSuggestions] = useState<MealSuggestion[] | null>(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");

  function load() {
    api.coachToday().then(setC).catch(() => {});
    api.supplements().then((r) => setSupps(r.supplements)).catch(() => {});
    api.coachNotes().then((r) => setReview(r.notes[0] ?? null)).catch(() => {});
    api.bloodPanels().then((r) => setPanels(r.panels)).catch(() => {});
  }
  useEffect(load, [reloadKey]);

  async function doPush() {
    setBusy("push");
    const res = await enablePush();
    setMsg(res.ok ? "Notifications on. I'll keep you honest." : res.reason || "Couldn't enable.");
    if (res.ok) { try { await api.pushTest(); } catch { /* ignore */ } }
    setBusy(""); setTimeout(() => setMsg(""), 4000);
  }
  async function getSuggestions() {
    setBusy("sug");
    try { const r = await api.suggestions(); setSuggestions(r.suggestions); }
    catch { setMsg("Couldn't load suggestions."); setTimeout(() => setMsg(""), 3000); }
    finally { setBusy(""); }
  }
  async function genReview() {
    setBusy("rev");
    try { await api.generateReview(); load(); }
    finally { setBusy(""); }
  }
  async function toggleSupp(id: number) {
    await api.toggleSupplement(id); load();
  }
  async function uploadBlood(file: File) {
    setBusy("blood");
    try {
      const b64 = await fileToBase64(file);
      const r = await api.bloodUpload(b64, file.name);
      setMsg(`Extracted ${r.markers} markers from ${r.panel_date}.`);
      load();
    } catch { setMsg("Couldn't read that PDF."); }
    finally { setBusy(""); setTimeout(() => setMsg(""), 4000); }
  }

  return (
    <div class="app-shell fade-in">
      <div class="screen-head"><div><div class="eyebrow">Insights</div><h1>Coach</h1></div></div>

      {/* Readiness */}
      {c && (
        <div class="card">
          <h2>Readiness · {c.readiness.mode} mode</h2>
          <div class="readiness">
            <Gauge score={c.readiness.score} verdict={c.readiness.verdict} />
            <div class="msg">{c.readiness.message}</div>
          </div>
          {c.readiness.drivers.length > 0 && (
            <div class="drivers">
              {c.readiness.drivers.map((d, i) => <span class={`driver ${d.impact}`} key={i}>{d.label}: {d.detail}</span>)}
            </div>
          )}
        </div>
      )}

      {/* Insights + minimum viable day */}
      {c && (
        <div class="card">
          <h2>Today</h2>
          <div class="mvd-row" style={{ marginBottom: 12 }}>
            <div class={`mvd-pill ${c.mvd.walk ? "on" : ""}`}>🐕 Walk</div>
            <div class={`mvd-pill ${c.mvd.food ? "on" : ""}`}>🍽️ Log</div>
            <div class={`mvd-pill ${c.mvd.protein ? "on" : ""}`}>💪 Protein</div>
          </div>
          {c.insights.length === 0
            ? <p class="muted">All quiet. Keep stacking the days.</p>
            : c.insights.map((i) => <div class={`insight ${i.tone}`} key={i.id}><span class="dot2" /><span>{i.text}</span></div>)}
          {c.proteinLeft > 5 && (
            <button class="btn secondary" style={{ marginTop: 12 }} onClick={getSuggestions} disabled={busy === "sug"}>
              {busy === "sug" ? <span class="spinner" /> : `Close my ${c.proteinLeft}g protein gap →`}
            </button>
          )}
          {suggestions && suggestions.map((s, i) => (
            <div class="suggestion" key={i}>
              <div class="sn">{s.name} · {s.protein_g}g P · {s.kcal} kcal</div>
              <div class="sm">{s.why}</div>
            </div>
          ))}
        </div>
      )}

      {/* Supplements */}
      <div class="card">
        <h2>Supplements</h2>
        {supps.length === 0 ? <p class="muted">No supplements configured.</p> : supps.map((s) => (
          <div class="supp-row" key={s.id}>
            <div>
              <div class="nm">{s.name}</div>
              <div class="ds">{s.dose}{s.default_timing ? ` · ${s.default_timing}` : ""}</div>
              {s.prompt && <div class="prompt">{s.prompt}</div>}
            </div>
            <button class={`chain-check ${s.takenToday ? "on" : ""}`} onClick={() => toggleSupp(s.id)}>✓</button>
          </div>
        ))}
      </div>

      {/* Sunday review */}
      <div class="card">
        <h2>Sunday review</h2>
        {review ? (
          <>
            <p class="muted" style={{ marginBottom: 8 }}>{review.date}</p>
            <div class="review-text">{review.content}</div>
          </>
        ) : <p class="muted">Your first weekly review will appear here. It runs automatically each Sunday.</p>}
        <button class="btn ghost" style={{ marginTop: 12 }} onClick={genReview} disabled={busy === "rev"}>
          {busy === "rev" ? <span class="spinner" /> : "Generate this week's review now"}
        </button>
      </div>

      {/* Blood markers */}
      <div class="card">
        <h2>Blood markers</h2>
        {panels.length === 0 ? (
          <p class="muted">Upload a blood-test PDF and I'll extract your markers and track them over time. Informs — doesn't diagnose.</p>
        ) : panels.map((p) => (
          <div key={p.date} style={{ marginBottom: 14 }}>
            <p class="muted" style={{ marginBottom: 6 }}>{p.date}{p.notes ? ` · ${p.notes}` : ""}</p>
            {p.markers.map((m, i) => (
              <div class="marker-row" key={i}>
                <span>{m.marker}</span>
                <span>
                  <span class="mv">{m.value}{m.unit ? ` ${m.unit}` : ""}</span>
                  {m.flag && m.flag !== "normal" && <span class={`flagpill ${m.flag}`}>{m.flag}</span>}
                </span>
              </div>
            ))}
          </div>
        ))}
        <label class="btn secondary" style={{ marginTop: 6 }}>
          {busy === "blood" ? <span class="spinner" /> : "Upload blood-test PDF"}
          <input type="file" accept="application/pdf" style={{ display: "none" }}
            onChange={(e) => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) uploadBlood(f); }} />
        </label>
      </div>

      {/* Notifications */}
      <div class="card">
        <h2>Notifications</h2>
        <p class="muted" style={{ marginBottom: 12 }}>
          Morning sync, evening lift, protein gap, wind-down, and the Sunday review — hard-mode nudges.
          {pushSupported() ? "" : " On iPhone, add the app to your Home Screen first, then enable."}
        </p>
        <button class="btn" onClick={doPush} disabled={busy === "push"}>
          {busy === "push" ? <span class="spinner" /> : "Enable notifications"}
        </button>
      </div>

      {msg && <div class="toast">{msg}</div>}
    </div>
  );
}
